/**
 * Question banks, questions, assessment configuration, and submission —
 * validated server-side per Master Brief §16.9. See MVP Spec §7.
 */
import { z } from "zod";
export const QUESTION_TYPES = [
    "SINGLE_MCQ",
    "MULTI_SELECT",
    "TRUE_FALSE",
    "SCENARIO_CHOICE",
];
export const SELECTION_MODES = ["RANDOM_SAMPLE", "SHUFFLE_ALL"];
export const ON_FINAL_FAILURE = ["REMEDIATION", "ADMIN_RESET", "IMMEDIATE_RETRY"];
// A DIAGNOSTIC bank feeds the pre/post score (see diagnostic.ts), not the
// graded final assessment — same authoring/import/publish surface either way.
export const QUESTION_BANK_PURPOSES = ["ASSESSMENT", "DIAGNOSTIC"];
// --- Question bank -----------------------------------------------------
export const createQuestionBankSchema = z.object({
    title: z.string().trim().min(1).max(200),
    trackId: z.string().min(1),
    purpose: z.enum(QUESTION_BANK_PURPOSES).default("ASSESSMENT"),
});
// --- Question ------------------------------------------------------------
const optionSchema = z.object({
    id: z.string().min(1).max(60),
    text: z.string().trim().min(1).max(1000),
});
const baseQuestionFields = {
    type: z.enum(QUESTION_TYPES),
    stem: z.string().trim().min(1).max(4000),
    options: z.array(optionSchema).min(2).max(10),
    correctOptionIds: z.array(z.string().min(1)).min(1).max(10),
    explanation: z.string().trim().max(2000).optional(),
    category: z.string().trim().max(100).optional(),
    difficulty: z.string().trim().max(50).optional(),
};
function refineQuestion(v, ctx) {
    const optionIds = new Set(v.options.map((o) => o.id));
    if (new Set(v.correctOptionIds).size !== v.correctOptionIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["correctOptionIds"],
            message: "Duplicate correct option ids",
        });
    }
    for (const id of v.correctOptionIds) {
        if (!optionIds.has(id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["correctOptionIds"],
                message: `Unknown option id: ${id}`,
            });
        }
    }
    if ((v.type === "SINGLE_MCQ" || v.type === "SCENARIO_CHOICE" || v.type === "TRUE_FALSE") &&
        v.correctOptionIds.length !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["correctOptionIds"],
            message: `${v.type} needs exactly one correct option`,
        });
    }
    if (v.type === "TRUE_FALSE" && v.options.length !== 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["options"],
            message: "TRUE_FALSE needs exactly two options",
        });
    }
}
export const createQuestionSchema = z
    .object({ bankId: z.string().min(1), ...baseQuestionFields })
    .superRefine((v, ctx) => refineQuestion(v, ctx));
export const updateQuestionSchema = z
    .object({
    type: baseQuestionFields.type.optional(),
    stem: baseQuestionFields.stem.optional(),
    options: baseQuestionFields.options.optional(),
    correctOptionIds: baseQuestionFields.correctOptionIds.optional(),
    explanation: baseQuestionFields.explanation,
    category: baseQuestionFields.category,
    difficulty: baseQuestionFields.difficulty,
})
    .superRefine((v, ctx) => {
    if (v.type && v.options && v.correctOptionIds) {
        refineQuestion({ type: v.type, options: v.options, correctOptionIds: v.correctOptionIds }, ctx);
    }
});
export const bulkSetQuestionsPublishedSchema = z.object({
    ids: z.array(z.string().min(1)).min(1).max(500),
    publish: z.boolean(),
});
// --- Assessment config ---------------------------------------------------
export const assessmentConfigSchema = z.object({
    bankId: z.string().min(1),
    passPercent: z.number().int().min(1).max(100).default(70),
    maxAttempts: z.number().int().min(1).max(10).default(2),
    selectionMode: z.enum(SELECTION_MODES).default("RANDOM_SAMPLE"),
    questionsPresented: z.number().int().min(1).max(200).default(20),
    randomiseOrder: z.boolean().default(true),
    onFinalFailure: z.enum(ON_FINAL_FAILURE).default("REMEDIATION"),
});
// --- Submission ------------------------------------------------------------
export const submitAssessmentSchema = z.object({
    responses: z
        .array(z.object({
        questionId: z.string().min(1),
        selectedOptionIds: z.array(z.string().min(1)).max(10),
    }))
        .min(1)
        .max(200),
});
//# sourceMappingURL=assessment.js.map